import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { FindingCitation } from 'types/findings';
import { CitationAssetDescriptor, CitationAssetKind } from 'types/citationAsset';
import {
  CitationAssetErrorResult,
  getCitationAssetContent,
  getCitationAssetMetadata,
} from 'services/citationAssetService';
import './CitationEvidencePanel.css';

export interface CitationEvidencePanelProps {
  citation: FindingCitation;
  token?: string;
  address?: string | null;
}

type EvidenceState =
  | { kind: CitationAssetKind; status: 'loading' }
  | { kind: CitationAssetKind; status: 'text'; descriptor: CitationAssetDescriptor; text: string }
  | { kind: CitationAssetKind; status: 'image'; descriptor: CitationAssetDescriptor; objectUrl: string }
  | {
      kind: CitationAssetKind;
      status: 'metadata-only';
      descriptor: CitationAssetDescriptor;
    }
  | { kind: CitationAssetKind; status: 'error'; error: CitationAssetErrorResult };

const KIND_LABELS: Record<CitationAssetKind, string> = {
  text: 'Text',
  table: 'Table',
  figure: 'Figure',
};

const EVIDENCE_KIND_ORDER: CitationAssetKind[] = ['text', 'table', 'figure'];

function evidenceAltText(descriptor: CitationAssetDescriptor, kind: CitationAssetKind): string {
  const page = descriptor.page ? `, page ${descriptor.page}` : '';
  return `${KIND_LABELS[kind]} evidence from ${descriptor.source_code} ${descriptor.edition}${page}`;
}

/**
 * Renders governed text/table/figure evidence for a single citation anchor
 * using the US-266 citation asset API. Only renders when the citation
 * carries `package_version` + `asset_ids` so citations without resolved
 * asset evidence (older citations, or citations the resolver could not
 * anchor to a package asset) keep the existing excerpt-only presentation
 * unchanged (WI-267 backward-compatibility AC).
 *
 * Every state (loading, text, image, licence-blocked, missing, unsupported,
 * retired, tamper-detected) renders deterministic, privacy-safe copy with
 * no raw server error text, and exposes an accessible name/role so screen
 * readers announce availability and fallback states.
 */
export const CitationEvidencePanel: FunctionComponent<CitationEvidencePanelProps> = ({
  citation,
  token,
  address = null,
}) => {
  const assetIds = citation.asset_ids;
  const packageVersion = citation.package_version;

  const requestedKinds = useMemo<Array<{ kind: CitationAssetKind; assetId: string }>>(() => {
    if (!assetIds || !packageVersion) {
      return [];
    }
    return EVIDENCE_KIND_ORDER
      .filter((kind) => Boolean(assetIds[kind]))
      .map((kind) => ({ kind, assetId: assetIds[kind] as string }));
  }, [assetIds, packageVersion]);

  const [states, setStates] = useState<Record<string, EvidenceState>>({});

  useEffect(() => {
    if (requestedKinds.length === 0) {
      return;
    }

    let cancelled = false;
    const objectUrls: string[] = [];

    setStates(
      Object.fromEntries(requestedKinds.map(({ kind }) => [kind, { kind, status: 'loading' } as EvidenceState]))
    );

    requestedKinds.forEach(async ({ kind, assetId }) => {
      const key = { urn: citation.urn, packageVersion: packageVersion!, assetId };

      const metadataResult = await getCitationAssetMetadata(key, token, address);
      if (cancelled) return;

      if (metadataResult.status === 'error') {
        setStates((prev) => ({ ...prev, [kind]: { kind, status: 'error', error: metadataResult } }));
        return;
      }

      const { descriptor } = metadataResult;
      const contentResult = await getCitationAssetContent(key, token, address);
      if (cancelled) return;

      if (contentResult.status === 'error') {
        if (contentResult.code === 'citation_asset_unsupported_media') {
          setStates((prev) => ({ ...prev, [kind]: { kind, status: 'metadata-only', descriptor } }));
          return;
        }
        setStates((prev) => ({ ...prev, [kind]: { kind, status: 'error', error: contentResult } }));
        return;
      }

      if (descriptor.mime_type.startsWith('text/')) {
        const text = new TextDecoder('utf-8').decode(contentResult.bytes);
        setStates((prev) => ({ ...prev, [kind]: { kind, status: 'text', descriptor, text } }));
        return;
      }

      if (descriptor.mime_type.startsWith('image/')) {
        const objectUrl = URL.createObjectURL(new Blob([contentResult.bytes], { type: descriptor.mime_type }));
        objectUrls.push(objectUrl);
        setStates((prev) => ({ ...prev, [kind]: { kind, status: 'image', descriptor, objectUrl } }));
        return;
      }

      setStates((prev) => ({ ...prev, [kind]: { kind, status: 'metadata-only', descriptor } }));
    });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citation.urn, packageVersion, requestedKinds, token, address]);

  if (requestedKinds.length === 0) {
    return null;
  }

  return (
    <div className="citation-evidence" data-testid="citation-evidence-panel">
      <span className="citation-details__label">Evidence</span>
      <ul className="citation-evidence__list">
        {requestedKinds.map(({ kind }) => {
          const state = states[kind];
          return (
            <li
              key={kind}
              className="citation-evidence__item"
              role="group"
              aria-label={`${KIND_LABELS[kind]} evidence`}
              data-testid={`citation-evidence-${kind}`}
              data-evidence-status={state?.status ?? 'loading'}
            >
              <span className="citation-evidence__kind">{KIND_LABELS[kind]}</span>
              {!state || state.status === 'loading' ? (
                <p className="citation-evidence__status" aria-live="polite">
                  Loading {KIND_LABELS[kind].toLowerCase()} evidence…
                </p>
              ) : state.status === 'text' ? (
                <blockquote className="citation-evidence__text">{state.text}</blockquote>
              ) : state.status === 'image' ? (
                <img
                  className="citation-evidence__image"
                  src={state.objectUrl}
                  alt={evidenceAltText(state.descriptor, kind)}
                />
              ) : state.status === 'metadata-only' ? (
                <p className="citation-evidence__fallback" role="status">
                  {KIND_LABELS[kind]} evidence metadata is available ({state.descriptor.source_code}{' '}
                  {state.descriptor.edition}
                  {state.descriptor.page ? `, page ${state.descriptor.page}` : ''}), but no displayable
                  content is provided for this asset type.
                </p>
              ) : (
                <p
                  className="citation-evidence__fallback citation-evidence__fallback--error"
                  role="status"
                >
                  {state.error.message}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
